/**
 * The wire shape of an org's plugin-registered sharing choices, kept in
 * its own leaf module so `organization.types.ts` can reference it without
 * importing the resolution helpers (which reach back into the org types
 * through the sharing policy). See `session-sharing-settings.ts` for what
 * the values mean and how they are read.
 *
 * @module @reflex/shared/session-sharing-settings.types
 */

import { z } from 'zod';

/** `<plugin>:<name>`, both kebab-case. Namespaced so two plugins cannot collide. */
export const SESSION_SHARING_SETTING_KEY_PATTERN =
  /^[a-z][a-z0-9]*(-[a-z0-9]+)*:[a-z][a-z0-9]*(-[a-z0-9]+)*$/;

/**
 * An org's stored choices, keyed by `SessionSharingSettingKey`. A key with
 * no entry has never been chosen and reads as the setting's declared
 * default. Keys are shape-checked here and membership-checked against the
 * composed plugin set when written, so the column only ever holds keys some
 * plugin declared.
 */
export const SessionSharingSettingsSchema = z.record(
  z.string().regex(SESSION_SHARING_SETTING_KEY_PATTERN, {
    message: "Sharing setting keys are formatted as '<plugin>:<name>' in lower-case kebab",
  }),
  z.boolean(),
);
export type SessionSharingSettings = z.infer<typeof SessionSharingSettingsSchema>;
